import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CodeBlock } from './CodeBlock';
import { getSdkDocMarkdown, resolveResource, type ResourceKey } from '../sdk-docs/content';

interface SdkDocsPanelProps {
  pathname?: string;
  docKey?: ResourceKey;
}

const markdownComponents: Components = {
  h1: ({ node: _node, ...props }) => (
    <h1 className="text-2xl font-semibold mb-3" style={{ color: 'var(--clique-fg)' }} {...props} />
  ),
  h2: ({ node: _node, ...props }) => (
    <h2
      className="text-lg font-semibold mt-6 mb-2"
      style={{ color: 'var(--clique-fg)' }}
      {...props}
    />
  ),
  h3: ({ node: _node, ...props }) => (
    <h3
      className="text-base font-semibold mt-4 mb-2"
      style={{ color: 'var(--clique-fg)' }}
      {...props}
    />
  ),
  p: ({ node: _node, ...props }) => <p className="mb-3 leading-relaxed" {...props} />,
  ul: ({ node: _node, ...props }) => <ul className="list-disc pl-5 mb-3 space-y-1" {...props} />,
  ol: ({ node: _node, ...props }) => <ol className="list-decimal pl-5 mb-3 space-y-1" {...props} />,
  li: ({ node: _node, ...props }) => <li className="leading-relaxed" {...props} />,
  a: ({ node: _node, ...props }) => (
    <a
      className="underline"
      style={{ color: 'var(--clique-primary)' }}
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  strong: ({ node: _node, ...props }) => (
    <strong className="font-semibold" style={{ color: 'var(--clique-fg)' }} {...props} />
  ),
  blockquote: ({ node: _node, ...props }) => (
    <blockquote
      className="pl-3 my-3 italic"
      style={{ borderLeft: '3px solid var(--clique-border)', color: 'var(--clique-fg-muted)' }}
      {...props}
    />
  ),
  hr: ({ node: _node, ...props }) => (
    <hr className="my-5" style={{ borderColor: 'var(--clique-border)' }} {...props} />
  ),
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto mb-4">
      <table className="w-full text-sm border-collapse" {...props} />
    </div>
  ),
  th: ({ node: _node, ...props }) => (
    <th
      className="px-3 py-2 text-left font-semibold"
      style={{
        border: '1px solid var(--clique-border)',
        backgroundColor: 'var(--clique-bg-muted)',
        color: 'var(--clique-fg)',
      }}
      {...props}
    />
  ),
  td: ({ node: _node, ...props }) => (
    <td
      className="px-3 py-2 align-top"
      style={{ border: '1px solid var(--clique-border)' }}
      {...props}
    />
  ),
  pre: ({ children }) => <>{children}</>,
  code: ({ node: _node, className, children, ...rest }) => {
    const isBlock = typeof className === 'string' && className.startsWith('language-');
    if (isBlock) {
      return <CodeBlock code={String(children).replace(/\n$/, '')} />;
    }
    return (
      <code
        className="px-1 py-0.5 rounded text-[0.85em]"
        style={{ backgroundColor: 'var(--clique-bg-muted)', color: 'var(--clique-fg)' }}
        {...rest}
      >
        {children}
      </code>
    );
  },
};

export function SdkDocsPanel({ pathname, docKey }: SdkDocsPanelProps) {
  const key = docKey ?? resolveResource(pathname ?? '/').key;
  const markdown = getSdkDocMarkdown(key);

  return (
    <div className="max-w-3xl text-sm" style={{ color: 'var(--clique-fg-secondary)' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
